import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import avatar from 'flarum/common/helpers/avatar';
import username from 'flarum/common/helpers/username';
import icon from 'flarum/common/helpers/icon';
import { truncate } from 'flarum/common/utils/string';
import humanTime from 'flarum/common/helpers/humanTime';

export default class RelatedTopics extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.discussion = this.attrs.discussion;
    this.relatedDiscussions = [];
    this.loading = true;
    this.loadRelatedTopics();
  }

  loadRelatedTopics() {
    const discussion = this.discussion;
    
    // 获取讨论的标签
    const tags = discussion.tags ? discussion.tags() : [];
    
    if (!tags || tags.length === 0) {
      this.loading = false;
      m.redraw();
      return;
    }

    // 使用第一个标签来查找同标签的讨论
    const tag = tags[0];
    const tagSlug = tag.slug ? tag.slug() : null;
    const tagId = tag.id ? tag.id() : null;
    
    if (!tagSlug && !tagId) {
      this.loading = false;
      m.redraw();
      return;
    }

    // 获取同标签的其他讨论
    // 优先使用 tag slug，如果没有则使用 tag id
    const filterValue = tagSlug || tagId;
    
    app.store
      .find('discussions', {
        filter: {
          tag: filterValue,
        },
        page: { limit: 10 }, // 获取更多，以便过滤后仍有足够数量
        sort: '-createdAt',
      })
      .then((results) => {
        // 排除当前讨论和付费课程
        this.relatedDiscussions = results
          .filter((d) => {
            // 排除当前讨论
            if (d.id() === discussion.id()) {
              return false;
            }
            
            // 排除付费课程：检查讨论的 groupCourse 属性
            const groupCourse = d.attribute ? d.attribute('groupCourse') : null;
            if (groupCourse && groupCourse.courseType === 'paid') {
              // 如果是付费课程，排除
              return false;
            }
            
            return true;
          })
          .slice(0, 3);
        this.loading = false;
        m.redraw();
      })
      .catch((error) => {
        console.error('Error loading related topics:', error);
        this.loading = false;
        m.redraw();
      });
  }

  view() {
    // 始终渲染容器，即使没有数据也显示（用于调试）
    return (
      <li className="RelatedTopics-item-wrapper">
          <h3 className="RelatedTopics-title">
            {app.translator.trans('tf-custom-discussions-details.forum.related_topics')}
          </h3>
        <div className="RelatedTopics">
        
          {this.loading ? (
            <div className="RelatedTopics-loading">
              {app.translator.trans('tf-custom-discussions-details.forum.loading')}
            </div>
          ) : !this.relatedDiscussions || this.relatedDiscussions.length === 0 ? (
            <div className="RelatedTopics-empty">
              {app.translator.trans('tf-custom-discussions-details.forum.no_related_topics')}
            </div>
          ) : (
            <div className="RelatedTopics-list">
              {this.relatedDiscussions.map((discussion) => this.discussionItem(discussion))}
            </div>
          )}
        </div>
      </li>
    );
  }

  // 辅助函数：生成首字母头像
  renderInitialAvatar(name, size = 20) {
    const displayName = name || '';
    const firstLetter = displayName.charAt(0).toUpperCase();
    return (
      <span
        className="RelatedTopics-item-avatar RelatedTopics-item-avatar-text"
        title={displayName}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#a0e5aa',
          color: 'white',
          borderRadius: '50%',
          fontSize: `${Math.floor(size * 0.4)}px`,
          fontWeight: 'bold'
        }}
      >
        {firstLetter}
      </span>
    );
  }

  discussionItem(discussion) {
    const user = discussion.user ? discussion.user() : null;
    const firstPost = discussion.firstPost ? discussion.firstPost() : null;
    const contentPlain = firstPost && firstPost.contentPlain ? firstPost.contentPlain() : '';
    const excerpt = contentPlain ? truncate(contentPlain, 150) : '';
    
    // 获取点赞数
    let likesCount = 0;
    if (firstPost) {
      if (typeof firstPost.likesCount === 'function') {
        likesCount = firstPost.likesCount() || 0;
      } else if (typeof firstPost.likesCount === 'number') {
        likesCount = firstPost.likesCount;
      } else if (firstPost.data && firstPost.data.attributes) {
        likesCount = firstPost.data.attributes.likesCount || 0;
      }
    }
    
    // 获取评论数
    const commentCount = discussion.commentCount ? (discussion.commentCount() - 1) : 0;
    
    // 获取回复用户（用于显示头像）
    const replyUsers = discussion.data && discussion.data.attributes ? (discussion.data.attributes.replyUsers || []) : [];
    const displayUsers = replyUsers.slice(0, 5);

    // 检查用户是否有头像
    let userAvatarUrl = null;
    if (user) {
      try {
        userAvatarUrl = user.avatarUrl ? user.avatarUrl() : null;
        // 如果头像URL为空字符串或null，视为无头像
        if (!userAvatarUrl || userAvatarUrl.trim() === '') {
          userAvatarUrl = null;
        }
      } catch (e) {
        userAvatarUrl = null;
      }
    }
    const userDisplayName = user ? (user.displayName ? user.displayName() : (user.username ? user.username() : '')) : '';

    return (
      <div className="RelatedTopics-item" key={discussion.id()}>
        <Link href={app.route.discussion(discussion)} className="RelatedTopics-item-link">
          <h4 className="RelatedTopics-item-title">{discussion.title()}</h4>
          
          <div className="RelatedTopics-item-meta">
            {user && (
              <div className="RelatedTopics-item-author">
                {userAvatarUrl ? avatar(user, { size: 20 }) : this.renderInitialAvatar(userDisplayName, 20)}
                <span className="RelatedTopics-item-author-name">{username(user)}</span>
                <span className="RelatedTopics-item-time">
                  {humanTime(discussion.createdAt())}
                </span>
              </div>
            )}
          </div>

          {excerpt && (
            <p className="RelatedTopics-item-excerpt">{excerpt}</p>
          )}

          <div className="RelatedTopics-item-stats">
            <div className="RelatedTopics-item-stats-left">
              <span className="RelatedTopics-item-likes">
                {icon('fa-light fa-heart')}
                <span className="count">{likesCount}</span>
              </span>
              <span className="RelatedTopics-item-comments">
              <i class="fa-light fa-comments"></i>
                <span className="count">{commentCount}</span>
              </span>
            </div>
            
            {displayUsers.length > 0 && (
              <div className="RelatedTopics-item-avatars">
                {displayUsers.map((userData, index) => {
                  const displayName = userData.displayName || userData.username || '';
                  if (userData.avatarUrl) {
                    const userModel = {
                      id: () => userData.id,
                      displayName: () => userData.displayName || userData.username,
                      username: () => userData.username,
                      avatarUrl: () => userData.avatarUrl,
                    };
                    return (
                      <span key={index} className="RelatedTopics-item-avatar" title={displayName}>
                        {avatar(userModel, { size: 20 })}
                      </span>
                    );
                  } else {
                    // 没有头像时显示首字母
                    return (
                      <span key={index} className="RelatedTopics-item-avatar" title={displayName}>
                        {this.renderInitialAvatar(displayName, 20)}
                      </span>
                    );
                  }
                })}
              </div>
            )}
          </div>
        </Link>
      </div>
    );
  }
}

